<template>
  <div
    class="app-select"
    :class="`app-select--${type}`"
  >
    <label
      v-if="label"
      :for="id"
      class="app-select__label"
    >
      {{ label }}
    </label>
    <div class="app-select__select-wrapper">
      <select
        :id="id"
        class="app-select__select"
        :class="{
          'app-select__select--error': showError,
          'app-select__select--valid': !showError && touched && !isFocused && rules,
        }"
        :value="modelValue"
        :name="name"
        :aria-label="ariaLabel || label || undefined"
        :disabled="disabled"
        :autocomplete="autocomplete"
        @focus="handleFocused"
        @blur="handleBlurred"
        @input="handleInput"
      >
        <option
          v-if="placeholderOption"
          value=""
          disabled
          selected
        >
          {{ placeholderOption }}
        </option>
        <option
          v-for="option in options"
          :key="option.key"
          :value="option.value ? option.value : option"
        >
          {{ option.key ? option.key : option }}
        </option>
      </select>
      <icon
        name="line-md:chevron-small-down"
        class="app-select__select-icon"
      />
    </div>
    <transition name="fade-quick">
      <div
        v-if="showError"
        class="app-select__form-error error-message"
      >
        {{ error }}
      </div>
    </transition>
  </div>
</template>

<script setup>
  const props = defineProps({
    id: {
      type: String,
      required: true,
    },
    name: {
      type: String,
      default: '',
    },
    modelValue: {
      type: [String, Number],
      default: '',
    },
    label: {
      type: String,
      default: null,
    },
    // Accessible name for selects that have no visible <label> (e.g. icon-only / inline selects).
    ariaLabel: {
      type: String,
      default: null,
    },
    options: {
      type: Array,
      default: () => [],
    },
    placeholderOption: {
      type: String,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: null,
    },
    touched: {
      type: Boolean,
      default: false,
    },
    autocomplete: {
      type: String,
      default: null,
    },
    rules: {
      type: Object,
      default: null,
    },
    type: {
      type: String, // default | rounded
      default: 'default',
    },
  })

  const emit = defineEmits(['focus', 'blur', 'update:modelValue'])

  const isFocused = ref(false)
  const showError = computed(() => props.error && !isFocused.value && props.touched)

  function handleInput(event) {
    emit('update:modelValue', event.target.value)
  }

  function handleFocused(event) {
    isFocused.value = true
    emit('focus', event)
  }

  function handleBlurred(event) {
    isFocused.value = false
    emit('blur', event)
  }
</script>

<style lang="scss">
  .app-select {
    position: relative;
  }

  .app-select__select-wrapper {
    position: relative;
  }

  .app-select__select {
    -moz-appearance: none;
    -webkit-appearance: none;

    font-family: var(--font-family-default);
    font-size: 16px;
    padding: 6px 28px 6px 6px;
    width: 100%;
    border: 2px solid var(--color-primary-15);
    border-radius: var(--default-border-radius);
    color: var(--color-mine-shaft);
    min-height: 42px;
    background: white;
  }

  .app-select__select:focus {
    outline: none;
  }

  .app-select__select--error {
    border: 2px solid var(--color-error);
  }

  .app-select__select--valid {
    border-bottom-color: var(--color-success);
  }

  .app-select__select-icon {
    position: absolute;
    bottom: 0;
    right: 6px;
    transform: translateY(-50%);
    width: 20px;
    height: 20px;
  }

  .app-select__select:focus-visible {
    outline-color: var(--color-secondary);
    border-color: var(--color-secondary);
  }

  .app-select__label {
    font-size: 14px;
  }

  .app-select__form-error {
    text-align: right;
  }

  // Rounded
  .app-select--rounded .app-select__select {
    border-radius: 60px;
    padding: 6px 44px 6px 24px;
    font-weight: 600;
    border: 1px solid var(--color-primary);
  }

  .app-select--rounded .app-select__select-icon {
    right: 24px;
  }
</style>
